import { ReactNode, useMemo, useState } from "react";
import { InputText } from "@sellar-io/components";
import { useNavigate } from "react-router";
import { InputContainer } from "@sellar-io/components";
import { Icon, SpinnerLegacy } from "../../components";
import { useMediaQuery } from "usehooks-ts";
import { twMerge } from "tailwind-merge";
import * as ROUTES from "../../constants/routes";
import { useDebounce } from "use-debounce";
import {
  Badge,
  BadgeGreen,
  BadgeIndigo,
  BadgeZinc,
} from "../../components/badge/Badge";
import {
  Dropdown,
  DropdownButton,
  DropdownItem,
  DropdownItems,
} from "../../components/dropdown/Dropdown";
import { PriceListDeleteDialog } from "./components/PriceListDeleteDialog";
import { usePriceTierList } from "../../hooks/api/v2/priceTier/usePriceTierList";
import { pluralize } from "@sellar-io/common";
import { InfiniteList } from "../../components/infiniteLoading/InfiniteList";
import { BasePriceListCreateDialog } from "./components/BasePriceListCreateDialog";
import { DerivedPriceListCreateDialog } from "./components/DerivedPriceListCreateDialog";
import { PriceListGuide } from "./components/PriceListGuide";
import { Alert } from "../../components/alert/Alert";
import { CustomersToConvertAlert } from "../customer/customerList/CustomersToConvertAlert";
import { useSupplierRelationshipCount } from "../../hooks/api/v2/supplierRelationship/useSupplierRelationshipCount";
import { useCustomerListStore } from "../customer/customerList/CustomerListStore";
import { useShallow } from "zustand/shallow";
import { usePriceTierConvertToBase } from "../../hooks/api/v2/priceTier/usePriceTierConvertToBase";
import { PriceTierListResponseType } from "@sellar-io/api-v2/types";
import { usePriceTierGetStats } from "../../hooks/api/v2/priceTier/usePriceTierGetStats";
import {
  Anchor,
  Button,
  Card,
  CardContent,
  CardHeader,
  DataButton,
  H5,
  H6,
  ListItemIconTile,
  SingleFieldDialog,
  SingleFieldDialogImage,
  SingleFieldDialogHeader,
  SingleFieldDialogCTA,
} from "@sellar-io/components";
import { useRemoteFeatureFlags } from "../../hooks/useRemoteFeatureFlags";
import { usePriceTierSetDefault } from "../../hooks/api/v2/priceTier/usePriceTierSetDefault";
import { useIsSellarAdmin } from "../../hooks/useIsSellarAdmin";
import { usePriceTierMigrateDefaults } from "../../hooks/api/v2/priceTier/usePriceTierMigrateDefaults";
import { useSellarCurrentBusiness } from "../../hooks/useSellarCurrentBusiness.shared";
import { useSupplier } from "../../hooks/api/v2/supplier/useSupplier";
import { DefaultPriceListTooltip } from "./components/DefaultPriceListTooltip";
import { getPriceListDiscountSummary } from "./utils/priceListDiscountSummary";

export const PriceListLists = () => {
  const remoteFeatureFlags = useRemoteFeatureFlags();
  const isUnbundlingEnabled = remoteFeatureFlags.isActive(
    "unbundlingReleaseFinal",
  );

  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [createBasePriceListActive, setCreateBasePriceListActive] =
    useState(false);
  const [createDerivedPriceListActive, setCreateDerivedPriceListActive] =
    useState(false);
  const [priceListGuideDismissed, setPriceListGuideDismissed] = useState(false);

  const isAdmin = useIsSellarAdmin();

  const [debouncedSearch] = useDebounce(search, 500);

  const [deleteDialogActive, setDeleteDialogActive] = useState(false);
  const [selectedList, setSelectedList] = useState<{
    id: number;
    name: string;
    isDefault: boolean;
  } | null>(null);

  const [setDefaultTarget, setSetDefaultTarget] = useState<{
    id: number;
    name: string;
  } | null>(null);

  const isDesktop = useMediaQuery("(min-width: 1200px)");

  const { setHasDiscounts } = useCustomerListStore(
    useShallow((state) => ({ setHasDiscounts: state.setHasDiscounts })),
  );

  const { data: basePriceLists, isFetched } = usePriceTierList({
    includeCounts: true,
    query: [
      {
        type: "BasePriceTierId",
        modifier: "IS_NULL",
      },
      {
        type: "derivationTypeIncludes",
        value: ["base"],
        modifier: "IN",
      },
    ],
  });

  const flatBaseLists = useMemo(
    () => basePriceLists?.pages.flatMap((a) => a) ?? [],
    [basePriceLists],
  );

  const priceListsQuery = usePriceTierList({
    includeCounts: true,
    search: debouncedSearch,
    limit: 10,
    query: [
      {
        type: "derivationTypeIncludes",
        value: ["custom", "flat", "percentage"],
        modifier: "IN",
      },
    ],
  });

  const flatLists = useMemo(
    () => priceListsQuery.data?.pages.flat() ?? [],
    [priceListsQuery.data],
  );

  const hasMultipleDefaults = useMemo(
    () =>
      [...flatLists].some(
        (list) =>
          list.defaultType === "direct" ||
          list.defaultType === "sellarDistribution",
      ),
    [flatLists],
  );

  const connectionWithDiscountsCountQuery = useSupplierRelationshipCount(
    {
      query: [
        {
          type: "hasDiscounts",
          modifier: "EQUALS",
          value: true,
        },
      ],
    },
    {
      placeholderData: (previousData) => previousData,
      staleTime: 5 * 60 * 1000,
    },
  );
  const customersWithDiscountsCount =
    connectionWithDiscountsCountQuery.data ?? 0;

  const convertToBaseMutation = usePriceTierConvertToBase();
  const setDefaultMutation = usePriceTierSetDefault();

  const convertToBase = (id: number, name: string) => {
    convertToBaseMutation.mutate(
      {
        id,
        name,
      },
      {
        onSuccess: (response) => {
          navigate(ROUTES.PRICE_LIST.href({ priceListId: response.id }));
        },
      },
    );
  };

  const showPriceListGuide =
    isFetched && flatBaseLists.length === 0 && !priceListGuideDismissed;

  if (!isFetched) return <SpinnerLegacy />;

  if (showPriceListGuide)
    return (
      <PriceListGuide onDismiss={() => setPriceListGuideDismissed(true)} />
    );

  return (
    <>
      <div className="flex flex-col gap-8">
        {isAdmin && isUnbundlingEnabled && <MigratePriceTierDefaults />}
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col gap-1">
              <H5>Base price lists</H5>
              <span className="text-zinc-500">
                Foundation price to build your derived price lists from
              </span>
            </div>
            <div className="flex items-center gap-2">
              <DataButton
                className="flex items-center gap-1"
                border={true}
                onClick={() => setCreateBasePriceListActive(true)}
              >
                <Icon icon="PLUS" />
                New
              </DataButton>
            </div>
          </div>
          {flatBaseLists.length === 0 && (
            <Alert
              type="information"
              title="Get started by setting a base price list"
              message="Your base price list is the starting point from which you can derive your customer price lists. "
              className="w-full"
            >
              <Button
                color={"secondary"}
                className={"border-blue-300 bg-blue-100 text-blue-800"}
                onClick={() => setPriceListGuideDismissed(false)}
              >
                Get started
              </Button>
            </Alert>
          )}
          <div className="flex flex-col divide-y overflow-hidden rounded-lg border border-zinc-200 bg-white">
            {flatBaseLists.map((list, idx) => (
              <BaseListItem
                key={idx}
                priceList={list}
                onClick={() => {
                  setSelectedList({
                    id: list.id,
                    name: list.name,
                    isDefault: Boolean(list.defaultType),
                  });
                  setDeleteDialogActive(true);
                }}
                getStats={flatBaseLists.length < 4}
              />
            ))}
          </div>
        </div>
        <div className="flex flex-col gap-4">
          <div className="flex items-center justify-between gap-2">
            <div className="flex flex-col gap-1">
              <H5>Derived price lists</H5>
              <span className="text-zinc-500">
                Create custom pricing by applying discounts
              </span>
            </div>
            <div className="flex items-center gap-2">
              <div className="flex flex-col gap-2">
                <div className="flex items-center gap-2">
                  <div className="flex">
                    <InputContainer
                      className={twMerge(
                        "w-full flex-none py-[0.4rem]",
                        isDesktop && "max-w-[200px]",
                      )}
                    >
                      <InputText
                        required={false}
                        placeholder="Search..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                        className="w-full"
                      />
                    </InputContainer>
                  </div>
                </div>
              </div>
              <DataButton
                className="flex items-center gap-1"
                border={true}
                onClick={() => setCreateDerivedPriceListActive(true)}
              >
                <Icon icon="PLUS" />
                New
              </DataButton>
            </div>
          </div>

          {customersWithDiscountsCount > 0 && (
            <CustomersToConvertAlert
              numberOfCustomers={customersWithDiscountsCount}
              onClick={() => {
                setHasDiscounts(true);
                navigate(ROUTES.CUSTOMERS);
              }}
              active={false}
            />
          )}

          <InfiniteList
            query={priceListsQuery}
            className="mb-8 flex flex-col gap-2"
          >
            {flatLists.map((list, idx) => {
              const migrated =
                list.derivationType === "custom" && !list.BasePriceTierId;

              return (
                <ListItem
                  key={idx}
                  name={`${list.name}`}
                  migrated={migrated}
                  defaultType={list.defaultType}
                  numberOfCustomers={list.numberOfCustomers}
                  topLevelDiscounts={list.topLevelDiscounts}
                  perItemDiscounts={list.perItemDiscounts}
                  volumeDiscounts={list.volumeDiscountsCount}
                  volumeDiscountSets={list.volumeDiscountSetsCount}
                  showVolumeDiscountSets={isUnbundlingEnabled}
                  onClick={() =>
                    navigate(ROUTES.PRICE_LIST.href({ priceListId: list.id }))
                  }
                  hasMultipleDefaults={hasMultipleDefaults}
                  rightComponent={
                    <Dropdown>
                      <DropdownButton>
                        <Button size={"small"} color={"transparent"}>
                          <Icon
                            icon={"PENCIL"}
                            className="text-black"
                            size={0.7}
                          />
                        </Button>
                      </DropdownButton>
                      <DropdownItems>
                        <DropdownItem
                          className="border-b border-zinc-200 text-sm"
                          onClick={() =>
                            navigate(
                              ROUTES.PRICE_LIST.href({ priceListId: list.id }),
                            )
                          }
                        >
                          Edit
                        </DropdownItem>
                        <DropdownItem
                          className="border-b border-zinc-200 text-sm"
                          onClick={() =>
                            navigate(
                              ROUTES.PRICE_LIST.href({ priceListId: list.id }),
                              {
                                state: { launchAssignmentDialog: true },
                              },
                            )
                          }
                        >
                          Assign
                        </DropdownItem>
                        {migrated && (
                          <DropdownItem
                            className="border-b border-zinc-200 text-sm"
                            onClick={() => convertToBase(list.id, list.name)}
                          >
                            Create base price list
                          </DropdownItem>
                        )}
                        {isUnbundlingEnabled && (
                          <DropdownItem
                            className={twMerge(
                              "border-b border-zinc-200 text-sm",
                              list.defaultType &&
                                !hasMultipleDefaults &&
                                "cursor-not-allowed opacity-50",
                            )}
                            onClick={() => {
                              if (list.defaultType && !hasMultipleDefaults)
                                return;

                              setSetDefaultTarget({
                                id: list.id,
                                name: list.name,
                              });
                            }}
                          >
                            Mark as default
                          </DropdownItem>
                        )}
                        <DropdownItem
                          className="border-b border-zinc-200 text-sm text-red-700"
                          onClick={() => {
                            setSelectedList({
                              id: list.id,
                              name: list.name,
                              isDefault: Boolean(list.defaultType),
                            });
                            setDeleteDialogActive(true);
                          }}
                        >
                          Delete
                        </DropdownItem>
                      </DropdownItems>
                    </Dropdown>
                  }
                />
              );
            })}
          </InfiniteList>
        </div>
      </div>
      {selectedList && (
        <PriceListDeleteDialog
          priceListId={selectedList.id}
          priceListName={selectedList.name}
          isDefault={selectedList.isDefault}
          active={deleteDialogActive}
          setActive={setDeleteDialogActive}
          openNewPriceList={() => setCreateDerivedPriceListActive(true)}
        />
      )}
      <BasePriceListCreateDialog
        active={createBasePriceListActive}
        setActive={setCreateBasePriceListActive}
      />
      <DerivedPriceListCreateDialog
        active={createDerivedPriceListActive}
        setActive={setCreateDerivedPriceListActive}
      />
      <SetDefaultPriceListDialog
        setDefaultTarget={setDefaultTarget}
        onDismiss={() => setSetDefaultTarget(null)}
        hasMultipleDefaults={hasMultipleDefaults}
        isPending={setDefaultMutation.isPending}
        onConfirm={() => {
          if (!setDefaultTarget) return;

          setDefaultMutation.mutate(
            { id: setDefaultTarget.id },
            { onSuccess: () => setSetDefaultTarget(null) },
          );
        }}
      />
    </>
  );
};

const SetDefaultPriceListDialog = ({
  setDefaultTarget,
  onDismiss,
  hasMultipleDefaults,
  isPending,
  onConfirm,
}: {
  setDefaultTarget: { id: number; name: string } | null;
  onDismiss: () => void;
  hasMultipleDefaults: boolean;
  isPending: boolean;
  onConfirm: () => void;
}) => {
  const [checked, setChecked] = useState(false);

  return (
    <SingleFieldDialog
      key={setDefaultTarget?.id ?? "no-target"}
      active={!!setDefaultTarget}
      onDismiss={onDismiss}
      size={hasMultipleDefaults ? "large" : "small"}
    >
      {hasMultipleDefaults && (
        <SingleFieldDialogImage src="https://sellar.imgix.net/sellar-image-assets/app-assets/default-pricelist-example-lg.png" />
      )}
      <SingleFieldDialogHeader
        title={`Are you sure you want to make '${setDefaultTarget?.name}' your default price list?`}
        description={
          hasMultipleDefaults ? (
            <p>
              You will no longer have different default price lists for Local
              and Nationwide customers. All new customers will see '
              {setDefaultTarget?.name}' pricing on your storefront. You can set
              which volume discounts they see conditionally based on their
              delivery method chosen.
              <br />
              <br />
              <Anchor
                className="inline-flex gap-1 text-blue-600 underline"
                href="https://intercom.help/sellario/en/articles/12011312-supplier-guide-customer-zones"
                target="_blank"
                rel="noopener noreferrer"
              >
                Why should I only have one default price list?
                <Icon
                  className="no-underline"
                  icon="ARROW_TOP_RIGHT"
                  size={0.6}
                />
              </Anchor>
            </p>
          ) : (
            "This will be the pricing shown to all new customers on the Market who haven't been explicitly assigned any custom pricing"
          )
        }
        icon="INFORMATION"
        iconClassName="text-blue-600 bg-blue-50 mb-auto"
      />
      <SingleFieldDialogCTA className="items-center">
        <label className="mr-auto ml-4 flex cursor-pointer items-center gap-2">
          <input
            type="checkbox"
            checked={checked}
            onChange={(e) => setChecked(e.target.checked)}
          />
          I understand
        </label>

        <Button color="transparent" onClick={onDismiss}>
          Dismiss
        </Button>
        <Button onClick={onConfirm} loading={isPending} disabled={!checked}>
          Confirm
        </Button>
      </SingleFieldDialogCTA>
    </SingleFieldDialog>
  );
};

const BaseListItem = ({
  priceList,
  onClick,
  getStats,
}: {
  priceList: PriceTierListResponseType[number];
  onClick: () => void;
  getStats: boolean;
}) => {
  const navigate = useNavigate();
  const isDesktop = useMediaQuery("(min-width: 700px)");

  const { data: priceListStats } = usePriceTierGetStats(priceList.id, {
    enabled: getStats,
  });

  const nbMissingPrices = priceListStats?.missingPricesCount ?? 0;

  return (
    <div
      onClick={() =>
        navigate(ROUTES.PRICE_LIST.href({ priceListId: priceList.id }))
      }
      className="flex w-full cursor-pointer items-center"
    >
      <div
        className={twMerge(
          "flex px-4 py-3 text-sm font-medium",
          isDesktop
            ? "w-[200px] border-r border-zinc-200 bg-zinc-50"
            : "flex-col gap-1",
        )}
      >
        <div className="flex items-center gap-2">
          <Icon icon="TAG" className="flex-none" size={0.7} />
          {priceList.name}
          {!isDesktop && priceList.numberOfDerivedLists !== null && (
            <span className="text-xs font-normal">
              {priceList.numberOfDerivedLists} derived{" "}
              {pluralize("list", priceList.numberOfDerivedLists)}
            </span>
          )}
        </div>

        {getStats && nbMissingPrices > 0 && !isDesktop && (
          <BadgeIndigo icon="INFORMATION" className="text-xs">
            <span>
              {nbMissingPrices} {pluralize("product", nbMissingPrices)}{" "}
              <span className="font-normal">missing pricing</span>
            </span>
          </BadgeIndigo>
        )}
      </div>
      {isDesktop && priceList.numberOfDerivedLists !== null && (
        <div className="px-4">
          <p className="text-sm">
            {priceList.numberOfDerivedLists} derived{" "}
            {pluralize("list", priceList.numberOfDerivedLists)}
          </p>
        </div>
      )}
      <span className="mr-2 ml-auto flex flex-none items-center gap-2">
        {getStats && nbMissingPrices > 0 && isDesktop && (
          <BadgeIndigo icon="INFORMATION">
            <span>
              {nbMissingPrices} {pluralize("product", nbMissingPrices)}{" "}
              <span className="font-normal">missing pricing</span>
            </span>
          </BadgeIndigo>
        )}
        <div
          onClick={(e) => e.stopPropagation()}
          onMouseEnter={(e) => e.stopPropagation()}
        >
          <Dropdown>
            <DropdownButton>
              <Button size={"small"} color={"transparent"}>
                <Icon icon={"PENCIL"} className="text-black" size={0.7} />
              </Button>
            </DropdownButton>
            <DropdownItems>
              <DropdownItem
                className="border-b border-zinc-200 text-sm"
                onClick={() =>
                  navigate(
                    ROUTES.PRICE_LIST.href({ priceListId: priceList.id }),
                  )
                }
              >
                Edit
              </DropdownItem>
              <DropdownItem
                className="border-b border-zinc-200 text-sm text-red-700"
                onClick={onClick}
              >
                Delete
              </DropdownItem>
            </DropdownItems>
          </Dropdown>
        </div>
      </span>
    </div>
  );
};

const ListItem = ({
  name,
  migrated,
  defaultType,
  numberOfCustomers,
  topLevelDiscounts,
  perItemDiscounts,
  volumeDiscounts,
  volumeDiscountSets,
  showVolumeDiscountSets,
  onClick,
  rightComponent,
  hasMultipleDefaults,
}: {
  name: string;
  migrated: boolean;
  defaultType: PriceTierListResponseType[number]["defaultType"];
  numberOfCustomers: number | null;
  topLevelDiscounts: number | null;
  perItemDiscounts: number | null;
  volumeDiscounts: number | null;
  volumeDiscountSets: number | null;
  showVolumeDiscountSets: boolean;
  onClick: () => void;
  rightComponent?: ReactNode;
  hasMultipleDefaults: boolean;
}) => {
  return (
    <Card
      onClick={onClick}
      className="flex cursor-pointer items-center gap-4 bg-white p-4 hover:bg-zinc-50"
    >
      <ListItemIconTile
        icon="TAG_MULTIPLE_OUTLINE"
        className={migrated ? "text-zinc-400" : "text-zinc-800"}
      />
      <div className="flex w-full items-center gap-4 md:flex-nowrap">
        <div className={twMerge("flex flex-col", migrated && "opacity-60")}>
          <p className="font-semibold">
            {name} {migrated && "(migrated)"}
          </p>
          <p className="text-sm">
            {getPriceListDiscountSummary({
              counts: {
                topLevelDiscounts,
                perItemDiscounts,
                volumeDiscountsCount: volumeDiscounts,
                volumeDiscountSetsCount: volumeDiscountSets,
              },
              showVolumeDiscountSets,
              separator: ", ",
            })}
          </p>
        </div>
        <span className="ml-auto flex flex-none items-center gap-2">
          {defaultType === "default" && (
            <DefaultPriceListTooltip hasMultipleDefaults={hasMultipleDefaults}>
              <Badge>
                {hasMultipleDefaults ? (
                  <span className="flex items-center gap-1">
                    <Icon icon="INFORMATION" size={0.6} />
                    Nationwide default
                  </span>
                ) : (
                  "Default"
                )}
              </Badge>
            </DefaultPriceListTooltip>
          )}
          {defaultType === "direct" && (
            <DefaultPriceListTooltip hasMultipleDefaults={hasMultipleDefaults}>
              <Badge>
                <span className="flex items-center gap-1">
                  <Icon icon="INFORMATION" size={0.6} />
                  Local default
                </span>
              </Badge>
            </DefaultPriceListTooltip>
          )}
          {defaultType === "sellarDistribution" && (
            <DefaultPriceListTooltip hasMultipleDefaults={hasMultipleDefaults}>
              <Badge>
                <span className="flex items-center gap-1">
                  <Icon icon="INFORMATION" size={0.6} />
                  Sellar Distribution default
                </span>
              </Badge>
            </DefaultPriceListTooltip>
          )}
          {numberOfCustomers !== null &&
            (numberOfCustomers === 0 ? (
              <BadgeZinc icon="ACCOUNT_MULTIPLE">0</BadgeZinc>
            ) : (
              <BadgeGreen icon="ACCOUNT_MULTIPLE">
                {numberOfCustomers}
              </BadgeGreen>
            ))}
          <div
            onClick={(e) => e.stopPropagation()}
            onMouseEnter={(e) => e.stopPropagation()}
          >
            {rightComponent ?? <Icon icon="CHEVRON_RIGHT" size={1} />}
          </div>
        </span>
      </div>
    </Card>
  );
};

const MigratePriceTierDefaults = () => {
  const [checked, setChecked] = useState(false);
  const migratePriceTierDefaults = usePriceTierMigrateDefaults();

  const { business } = useSellarCurrentBusiness();

  const { data: supplier } = useSupplier(business?.Supplier?.id);

  if (!supplier || supplier.hasMigratedDefaultTypes) return <></>;

  return (
    <Card className="border border-red-500 bg-white">
      <CardHeader className="flex flex-col bg-white p-4">
        <H5>Migrate price list and product catalogue defaults</H5>
        <H6>This will set defaultType on price lists and product catalogues</H6>
      </CardHeader>
      <CardContent className="flex flex-col gap-4 p-4">
        <Alert
          type="information"
          title="Confirmation"
          message="This will set defaultType on price lists and product catalogues based on your default and direct customer zones."
          className="w-full"
        >
          <div className="flex flex-col gap-4">
            <label className="flex cursor-pointer items-center gap-2 rounded-lg border border-blue-300 bg-blue-200 p-2 hover:bg-blue-300">
              <input
                id="checkbox"
                type="checkbox"
                onChange={(e) => setChecked(e.target.checked)}
              />
              Check to confirm you understand
            </label>
          </div>
        </Alert>
        <Button
          disabled={!checked}
          loading={migratePriceTierDefaults.isPending}
          onClick={() => migratePriceTierDefaults.mutate()}
          className="w-full"
          color="red"
        >
          Migrate
        </Button>
      </CardContent>
    </Card>
  );
};
